import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaUniversity } from "react-icons/fa";
import { QrCode, RefreshCw } from "lucide-react";
import { BranchBankDetailsService } from "../../Commen_Services/BranchBankDetails.service";
import { ProfileMasterService } from "../../Commen_Services/ProfileMaster.service";
import { commanServicer } from "../../Commen_Services/common.services";
import customToast from "../../Commen_Component/Toast/CustomToast";
import { PageEnums } from "../../../_enums/enum.services";
import EnhancedPage from "../../Commen_Component/EnhancedPage";
import "./BranchBankDetails.css";

interface DropdownOption {
    id: number;
    name: string;
}

interface BranchApiOption {
    branchId: number;
    branchName: string;
}

interface FormValues {
    bankName: string;
    accountHolderName: string;
    accountNumber: string;
    confirmAccountNumber: string;
    ifscCode: string;
    accountType: string;
}

type FormErrors = Partial<Record<keyof FormValues | "companyId" | "branchId" | "qrCode", string>>;

const initialValues: FormValues = {
    bankName: "",
    accountHolderName: "",
    accountNumber: "",
    confirmAccountNumber: "",
    ifscCode: "",
    accountType: ""
};

const MAX_QR_SIZE = 100 * 1024;
const OTP_VALIDITY_SECONDS = 2 * 60;
const OTP_VERIFIED_PAUSE_MS = 2000;

function getBankDetailsSnapshot(
    companyId: number,
    branchId: number,
    isActive: boolean,
    values: FormValues,
    hasNewQrFile: boolean,
    qrCodePath: string
): string {
    return JSON.stringify({ companyId, branchId, isActive, values, hasNewQrFile, qrCodePath });
}

function assertApiSuccess(response: any, fallbackMessage?: string): void {
    if (response.statusCode !== 200 || response.status === false) {
        if (response.message) throw new Error(response.message);
        if (fallbackMessage) throw new Error(fallbackMessage);
        throw new Error();
    }
}

function readOption(
    item: Record<string, unknown>,
    candidates: string[],
    fallbackIndex: number,
    entityName: "company" | "branch"
): DropdownOption {
    const keys = Object.keys(item);
    const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, "");
    const normalizedCandidates = candidates.map(normalizeKey);
    const matchingKey = keys.find((key) => normalizedCandidates.includes(normalizeKey(key))) ||
        keys.find((key) => {
            const normalized = normalizeKey(key);
            return normalized.includes(entityName) && normalized.endsWith("id");
        });
    const id = matchingKey ? item[matchingKey] : item[keys[fallbackIndex]];
    const normalizedNameCandidates = ["optionname", `${entityName}name`, "name"].map(normalizeKey);
    const nameKey = keys.find((key) => normalizedNameCandidates.includes(normalizeKey(key)) && key !== matchingKey) ||
        keys.find((key) => normalizeKey(key).includes(entityName) && normalizeKey(key).endsWith("name"));
    const name = nameKey ? item[nameKey] : item[keys[fallbackIndex + 1]];
    return { id: Number(id), name: String(name ?? id ?? "") };
}

function validate(values: FormValues, companyId: number, branchId: number, hasQr: boolean): FormErrors {
    const errors: FormErrors = {};
    const bankName = values.bankName.trim();
    const holderName = values.accountHolderName.trim();
    const accountNumber = values.accountNumber.trim();
    const confirmAccountNumber = values.confirmAccountNumber.trim();
    const ifscCode = values.ifscCode.trim().toUpperCase();

    if (!companyId) errors.companyId = "Please select a Company.";
    if (!branchId) errors.branchId = "Please select a Branch.";

    if (!bankName) errors.bankName = "Bank Name is required.";
    else if (bankName.length > 100 || !/^[A-Za-z &.-]+$/.test(bankName)) {
        errors.bankName = "Only letters, spaces, &, . and - are allowed (maximum 100 characters).";
    }

    if (!holderName) errors.accountHolderName = "Account Holder Name is required.";
    else if (holderName.length > 100 || !/^[A-Za-z .]+$/.test(holderName)) {
        errors.accountHolderName = "Only letters and spaces are allowed.";
    }

    if (!accountNumber) errors.accountNumber = "Account Number is required.";
    else if (!/^\d+$/.test(accountNumber)) errors.accountNumber = "Account Number must be numeric.";
    else if (accountNumber.length < 9 || accountNumber.length > 18) {
        errors.accountNumber = "Account Number must be between 9 and 18 digits.";
    }

    if (!confirmAccountNumber) errors.confirmAccountNumber = "Confirm Account Number is required.";
    else if (!/^\d+$/.test(confirmAccountNumber)) {
        errors.confirmAccountNumber = "Account Number must be numeric.";
    } else if (confirmAccountNumber.length < 9 || confirmAccountNumber.length > 18) {
        errors.confirmAccountNumber = "Account Number must be between 9 and 18 digits.";
    } else if (confirmAccountNumber !== accountNumber) {
        errors.confirmAccountNumber = "Account numbers do not match.";
    }

    if (!ifscCode) errors.ifscCode = "IFSC Code is required.";
    else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
        errors.ifscCode = "Enter a valid 11-character IFSC Code (e.g., SBIN0001234).";
    }

    if (!values.accountType) errors.accountType = "Please select an Account Type.";
    else if (values.accountType !== "Current" && values.accountType !== "Savings") {
        errors.accountType = "Please select an Account Type.";
    }

    if (!hasQr) errors.qrCode = "Please upload a QR Code image.";
    return errors;
}

const BranchBankDetails: React.FC = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const { pageId, action } = useParams<{ pageId: string; action: string }>();
    const detailId = action === "view" || action === "edit" ||
        (!action && Number(pageId) > 0 && Number(pageId) !== PageEnums.BranchBankDetails)
        ? Number(pageId)
        : 0;
    const isViewMode = action === "view";
    const [companies, setCompanies] = useState<DropdownOption[]>([]);
    const [branches, setBranches] = useState<DropdownOption[]>([]);
    const [companyId, setCompanyId] = useState(0);
    const [branchId, setBranchId] = useState(0);
    const [isActive, setIsActive] = useState(true);
    const [values, setValues] = useState<FormValues>(initialValues);
    const [errors, setErrors] = useState<FormErrors>({});
    const [qrFile, setQrFile] = useState<File | null>(null);
    const [qrPreview, setQrPreview] = useState("");
    const [showQrPreview, setShowQrPreview] = useState(false);
    const [existingQrPath, setExistingQrPath] = useState("");
    const [bankConfigId, setBankConfigId] = useState(0);
    const [showOtp, setShowOtp] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpRemainingSeconds, setOtpRemainingSeconds] = useState(OTP_VALIDITY_SECONDS);
    const [otpStatus, setOtpStatus] = useState<"idle" | "verifying" | "verified" | "saving">("idle");
    const [notice, setNotice] = useState("");
    const [noticeType, setNoticeType] = useState<"success" | "error">("success");
    const [loading, setLoading] = useState(false);
    const [isSyncingBranches, setIsSyncingBranches] = useState(false);
    const otpExpiresAtRef = useRef<number | null>(null);
    const [initialBankDetails, setInitialBankDetails] = useState<string | null>(null);
    const isReportRoute = Number(pageId) === PageEnums.BranchBankDetails && !action;
    const selectedCompanyName = companies.find((company) => company.id === companyId)?.name;

    useEffect(() => {
        if (!detailId) {
            setBankConfigId(0);
            setInitialBankDetails(null);
            return;
        }

        let active = true;
        const loadBankDetails = async () => {
            setInitialBankDetails(null);
            setLoading(true);
            setNotice("");
            try {
                const response = await BranchBankDetailsService.getBankDetail(detailId);
                assertApiSuccess(response, "Unable to load bank details.");
                if (!response.data) throw new Error("Bank details were not found.");

                const detail = response.data;
                if (!active) return;
                const detailValues: FormValues = {
                    bankName: detail.bankName || "",
                    accountHolderName: detail.accountHolderName || "",
                    accountNumber: String(detail.accountNumber || ""),
                    confirmAccountNumber: String(detail.accountNumber || ""),
                    ifscCode: String(detail.ifscCode || "").toUpperCase(),
                    accountType: detail.accountType || ""
                };
                const detailCompanyId = Number(detail.companyId || 0);
                const detailBranchId = Number(detail.branchId || 0);
                const detailIsActive = detail.isActive ?? true;
                const detailQrCodePath = detail.qrCodePath || "";

                setBankConfigId(Number(detail.bankConfigId || detailId));
                setCompanyId(detailCompanyId);
                setBranchId(detailBranchId);
                setIsActive(detailIsActive);
                setValues(detailValues);
                setExistingQrPath(detailQrCodePath);
                setQrPreview(detailQrCodePath);
                setInitialBankDetails(getBankDetailsSnapshot(
                    detailCompanyId,
                    detailBranchId,
                    detailIsActive,
                    detailValues,
                    false,
                    detailQrCodePath
                ));
            } catch (error) {
                if (active) {
                    setNotice(error instanceof Error ? error.message : "Unable to load bank details.");
                    setNoticeType("error");
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        loadBankDetails();
        return () => { active = false; };
    }, [detailId, action]);

    useEffect(() => {
        if (isReportRoute) return;

        let active = true;
        const loadCompanies = async () => {
            try {
                const response = await BranchBankDetailsService.getCompanies();
                assertApiSuccess(response, "Unable to load companies.");
                const data = Array.isArray(response.data) ? response.data : [];
                if (active) setCompanies(data.map((item: Record<string, unknown>) => readOption(item, ["companyid", "optionid", "id"], 0, "company")));
            } catch (error) {
                if (active) {
                    setNotice(error instanceof Error ? error.message : "Unable to load companies.");
                    setNoticeType("error");
                }
            }
        };

        loadCompanies();
        return () => { active = false; };
    }, [isReportRoute]);

    useEffect(() => {
        if (!companyId) {
            setBranches([]);
            return;
        }

        let active = true;
        const loadBranches = async () => {
            setBranches([]);
            try {
                const response = await BranchBankDetailsService.getBranches(companyId);
                assertApiSuccess(response, "Unable to load branches.");
                const data = Array.isArray(response.data) ? response.data : [];
                if (active) {
                    setBranches(data.map((item: BranchApiOption) => ({
                        id: item.branchId,
                        name: item.branchName
                    })));
                }
            } catch (error) {
                if (active) {
                    setNotice(error instanceof Error ? error.message : "Unable to load branches.");
                    setNoticeType("error");
                }
            }
        };

        loadBranches();
        return () => { active = false; };
    }, [companyId]);

    useEffect(() => () => {
        if (qrPreview.startsWith("blob:")) URL.revokeObjectURL(qrPreview);
    }, [qrPreview]);

    const isUpdateDirty = initialBankDetails !== null &&
        initialBankDetails !== getBankDetailsSnapshot(
            companyId,
            branchId,
            isActive,
            values,
            qrFile !== null,
            existingQrPath
    );

    useEffect(() => {
        if (!showOtp || otpStatus === "verified" || otpStatus === "saving") return;

        const updateCountdown = () => {
            const expiresAt = otpExpiresAtRef.current;
            if (expiresAt === null) return;

            const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
            setOtpRemainingSeconds(remaining);
        };

        updateCountdown();
        const interval = window.setInterval(updateCountdown, 1000);
        return () => window.clearInterval(interval);
    }, [showOtp, otpStatus]);

    const clearForm = () => {
        setCompanyId(0);
        setBranchId(0);
        setIsActive(true);
        setBranches([]);
        setValues(initialValues);
        setErrors({});
        setQrFile(null);
        setQrPreview("");
        setShowQrPreview(false);
        setExistingQrPath("");
        setBankConfigId(0);
        setInitialBankDetails(null);
        setOtp("");
        setShowOtp(false);
        setOtpStatus("idle");
        setOtpRemainingSeconds(OTP_VALIDITY_SECONDS);
        otpExpiresAtRef.current = null;
        setNotice("");
    };

    const clearBankFields = () => {
        setIsActive(true);
        setValues(initialValues);
        setQrFile(null);
        setQrPreview("");
        setShowQrPreview(false);
        setExistingQrPath("");
        setErrors({});
        setNotice("");
    };

    const handleCompanyChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const nextCompanyId = Number(event.target.value);
        setCompanyId(nextCompanyId);
        setBranchId(0);
        clearBankFields();
    };

    const handleBranchChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const nextBranchId = Number(event.target.value);
        setBranchId(nextBranchId);
        clearBankFields();
    };

    const handleSyncBranches = async () => {
        setIsSyncingBranches(true);
        try {
            const response = await BranchBankDetailsService.syncBranches(companyId || undefined);
            assertApiSuccess(response);

            if (companyId) {
                const branchesResponse = await BranchBankDetailsService.getBranches(companyId);
                assertApiSuccess(branchesResponse);
                const data = Array.isArray(branchesResponse.data) ? branchesResponse.data : [];
                setBranches(data.map((item: BranchApiOption) => ({
                    id: item.branchId,
                    name: item.branchName
                })));
            }

            customToast.success(response.message);
        } catch (error) {
            customToast.error(error instanceof Error ? error.message : String(error));
        } finally {
            setIsSyncingBranches(false);
        }
    };

    const handleValueChange = (field: keyof FormValues, value: string) => {
        let sanitizedValue = value;
        switch (field) {
            case "bankName":
                sanitizedValue = value.replace(/[^A-Za-z &.-]/g, "");
                break;
            case "accountHolderName":
                sanitizedValue = value.replace(/[^A-Za-z .]/g, "");
                break;
            case "accountNumber":
            case "confirmAccountNumber":
                sanitizedValue = value.replace(/\D/g, "");
                break;
            case "ifscCode":
                sanitizedValue = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
                break;
        }

        setValues((current) => ({ ...current, [field]: sanitizedValue }));
        setErrors((current) => ({ ...current, [field]: "" }));
    };

    const handleQrChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        setNotice("");
        setQrFile(null);
        setQrPreview("");
        setExistingQrPath("");
        if (!["image/jpeg", "image/png"].includes(file.type)) {
            setErrors((current) => ({ ...current, qrCode: "Unsupported file format. Please upload a JPG or PNG image." }));
            return;
        }
        if (file.size > MAX_QR_SIZE) {
            setErrors((current) => ({ ...current, qrCode: "File size exceeds 100 KB. Please upload a smaller image." }));
            return;
        }

        const previewUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(previewUrl);
            if (image.width < 200 || image.height < 200) {
                setErrors((current) => ({ ...current, qrCode: "Image resolution must be at least 200 × 200 pixels." }));
                return;
            }
            setQrFile(file);
            setQrPreview(URL.createObjectURL(file));
            setErrors((current) => ({ ...current, qrCode: "" }));
        };
        image.onerror = () => {
            URL.revokeObjectURL(previewUrl);
            setErrors((current) => ({ ...current, qrCode: "Unable to read this image. Please choose a valid JPG or PNG file." }));
        };
        image.src = previewUrl;
    };

    const sendOtp = async (): Promise<boolean> => {
        setLoading(true);
        setNotice("");
        try {
            const response = await ProfileMasterService.sendOtp();
            if (response.statusCode !== 200 || response.status === false) {
                throw new Error(response.message);
            }
            if (response.data?.isOTPSent !== true) {
                throw new Error(response.message);
            }
            setOtp("");
            setOtpStatus("idle");
            setOtpRemainingSeconds(OTP_VALIDITY_SECONDS);
            otpExpiresAtRef.current = Date.now() + OTP_VALIDITY_SECONDS * 1000;
            setNotice("");
            customToast.success(response.message);
            return true;
        } catch (error) {
            customToast.error(error instanceof Error ? error.message : String(error));
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setNotice("");
        const nextErrors = validate(values, companyId, branchId, Boolean(qrFile || existingQrPath));
        setErrors(nextErrors);
        if (Object.values(nextErrors).some(Boolean)) {
            setNotice("Please fix the highlighted fields.");
            setNoticeType("error");
            return;
        }

        setLoading(true);
        try {
            const response = await BranchBankDetailsService.verifyBankDetails({
                companyId,
                branchId,
                bankConfigId
            });
            if (!response.status) {
                throw new Error(response.message);
            }
        } catch (error) {
            customToast.error(error instanceof Error ? error.message : String(error));
            return;
        } finally {
            setLoading(false);
        }

        if (await sendOtp()) setShowOtp(true);
    };

    const handleVerify = async () => {
        if (!/^\d{6}$/.test(otp)) {
            customToast.error("Please enter a 6-digit OTP.");
            return;
        }

        if (!qrFile && !existingQrPath) {
            setNotice("");
            customToast.error("Please upload a QR Code image.");
            setShowOtp(false);
            setErrors((current) => ({ ...current, qrCode: "Please upload a QR Code image." }));
            return;
        }

        setLoading(true);
        setOtpStatus("verifying");
        setNotice("");
        try {
            const otpResponse = await ProfileMasterService.verifyOtp(Number(otp));
            if (otpResponse.statusCode !== 200 || otpResponse.status === false) {
                throw new Error(otpResponse.message);
            }
            if (otpResponse.data?.isOTPVerified !== true) {
                throw new Error(otpResponse.message);
            }
            setOtpStatus("verified");
            setNotice("");
            await new Promise<void>((resolve) => window.setTimeout(resolve, OTP_VERIFIED_PAUSE_MS));
            setOtpStatus("saving");

            let uploadedPath = existingQrPath;
            if (qrFile) {
                const sanitizedName = qrFile.name.replace(/[^A-Za-z0-9._-]/g, "_");
                const uploadResult = await commanServicer.UploadImage(
                    "qrcodes/",
                    `bank_qr_${Date.now()}_${sanitizedName}`,
                    qrFile
                );
                if (typeof uploadResult !== "string" || !uploadResult) {
                    throw new Error("QR Code upload failed. Please try again.");
                }
                uploadedPath = uploadResult;
            }

            const payload = {
                bankConfigId,
                bankName: values.bankName.trim(),
                accountHolderName: values.accountHolderName.trim(),
                accountNumber: values.accountNumber.trim(),
                confirmAccountNumber: values.confirmAccountNumber.trim(),
                ifscCode: values.ifscCode.trim().toUpperCase(),
                accountType: values.accountType,
                qrCodePath: uploadedPath,
                companyId,
                branchId,
                isActive
            };
            const response = await BranchBankDetailsService.saveBankDetails(payload);
            assertApiSuccess(response, "Unable to save bank details.");

            setShowOtp(false);
            clearForm();
            customToast.success(otpResponse.message + " and " + response.message || "Record saved successfully.", { toastId: "branch-bank-otp-verified-save-success" });
            navigate(`/Master/BranchBankDetails/${PageEnums.BranchBankDetails}`);
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            setNotice("");
            customToast.error(message);
            setOtpStatus("idle");
        } finally {
            setLoading(false);
        }
    };

    const cancelForm = () => {
        clearForm();
        navigate(`/Master/BranchBankDetails/${PageEnums.BranchBankDetails}`);
    };

    if (isReportRoute) {
        return <EnhancedPage pageId={PageEnums.BranchBankDetails} />;
    }

    return (
        <main className="bank-page">
            <div className="shadow bank-page-title">
                <div className="bg-line">
                    <span className="bg-head">
                        <FaUniversity /> &nbsp;
                        {isViewMode ? "View" : detailId ? "Update" : "Add"} Branch Bank Details
                    </span>
                </div>
                <hr className="colorgraph" />
            </div>

            <div className="bank-form-heading">
                <div>
                    <h2>Company &amp; Branch Bank Details</h2>
                    <p>Select the company and branch, then enter the account information.</p>
                </div>
                <button type="button" className="bank-button secondary" onClick={cancelForm}>← Back to list</button>
            </div>
            <form className="bank-card bank-form" onSubmit={handleSubmit} noValidate>
                        <section className="bank-form-section">
                            <div className="bank-section-heading">
                                <div>
                                    <h3>Company &amp; Branch</h3>
                                    <p>Select a company first — the branch list will update to match.</p>
                                </div>
                                {!isViewMode && (
                                    <button
                                        type="button"
                                        className="bank-button secondary branch-sync-button"
                                        onClick={handleSyncBranches}
                                        disabled={isSyncingBranches}
                                        title={companyId ? `Re-sync branches for ${selectedCompanyName || "selected company"}` : "Sync branches for all active companies"}
                                    >
                                        <RefreshCw className={isSyncingBranches ? "branch-sync-icon is-syncing" : "branch-sync-icon"} aria-hidden="true" />
                                        <span>
                                            {isSyncingBranches
                                                ? "Syncing branches..."
                                                : companyId
                                                    ? `Re-sync for ${selectedCompanyName || "company"}`
                                                    : "Sync all company branches"}
                                        </span>
                                    </button>
                                )}
                            </div>
                            <div className="bank-two-column">
                                <label className="bank-field">
                                    <span>Company <b>*</b></span>
                                    <select value={companyId || ""} onChange={handleCompanyChange} disabled={isViewMode || isSyncingBranches} aria-invalid={Boolean(errors.companyId)}>
                                        <option value="">Select Company</option>
                                        {companies.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                                    </select>
                                    {errors.companyId && <small>{errors.companyId}</small>}
                                </label>
                                <label className="bank-field">
                                    <span>Branch <b>*</b></span>
                                    <select id="bank-branch-select" value={branchId || ""} onChange={handleBranchChange} disabled={!companyId || isViewMode || isSyncingBranches} aria-invalid={Boolean(errors.branchId)}>
                                        <option value="">Select Branch</option>
                                        {branches.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                                    </select>
                                    {errors.branchId && <small>{errors.branchId}</small>}
                                </label>
                            </div>
                        </section>

                        {companyId > 0 && branchId > 0 && (
                            <section className="bank-form-section bank-details-section">
                                <h3>Bank Details</h3>
                                <p>Enter the account {branches.find((item) => item.id === branchId)?.name} collects into, and attach its QR code.</p>
                                <div className="bank-details-grid">
                                    <div className="bank-fields-grid">
                                        <label className="bank-field">
                                            <span>Bank Name <b>*</b></span>
                                            <input value={values.bankName} maxLength={100} readOnly={isViewMode} onChange={(event) => handleValueChange("bankName", event.target.value)} aria-invalid={Boolean(errors.bankName)} />
                                            {errors.bankName && <small>{errors.bankName}</small>}
                                        </label>
                                        <label className="bank-field">
                                            <span>Account Holder Name <b>*</b></span>
                                            <input value={values.accountHolderName} maxLength={100} readOnly={isViewMode} onChange={(event) => handleValueChange("accountHolderName", event.target.value)} aria-invalid={Boolean(errors.accountHolderName)} />
                                            {errors.accountHolderName && <small>{errors.accountHolderName}</small>}
                                        </label>
                                        <label className="bank-field">
                                            <span>Account Number <b>*</b></span>
                                            <input value={values.accountNumber} inputMode="numeric" maxLength={18} readOnly={isViewMode} onChange={(event) => handleValueChange("accountNumber", event.target.value)} aria-invalid={Boolean(errors.accountNumber)} />
                                            {errors.accountNumber && <small>{errors.accountNumber}</small>}
                                        </label>
                                        <label className="bank-field">
                                            <span>Confirm Account Number <b>*</b></span>
                                            <input value={values.confirmAccountNumber} inputMode="numeric" maxLength={18} readOnly={isViewMode} onPaste={(event) => event.preventDefault()} onCopy={(event) => event.preventDefault()} onCut={(event) => event.preventDefault()} onChange={(event) => handleValueChange("confirmAccountNumber", event.target.value)} aria-invalid={Boolean(errors.confirmAccountNumber)} />
                                            {errors.confirmAccountNumber && <small>{errors.confirmAccountNumber}</small>}
                                        </label>
                                        <label className="bank-field">
                                            <span>IFSC Code <b>*</b></span>
                                            <input value={values.ifscCode} maxLength={11} readOnly={isViewMode} onChange={(event) => handleValueChange("ifscCode", event.target.value)} onBlur={() => handleValueChange("ifscCode", values.ifscCode.toUpperCase())} aria-invalid={Boolean(errors.ifscCode)} />
                                            {errors.ifscCode && <small>{errors.ifscCode}</small>}
                                        </label>
                                        <label className="bank-field">
                                            <span>Account Type <b>*</b></span>
                                            <select value={values.accountType} disabled={isViewMode} onChange={(event) => handleValueChange("accountType", event.target.value)} aria-invalid={Boolean(errors.accountType)}>
                                                <option value="">Select Account Type</option>
                                                <option value="Current">Current</option>
                                                <option value="Savings">Savings</option>
                                            </select>
                                            {errors.accountType && <small>{errors.accountType}</small>}
                                        </label>
                                        <div className="bank-active-field">
                                            <label className="bank-active-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={isActive}
                                                    onChange={(event) => setIsActive(event.target.checked)}
                                                    disabled={isViewMode}
                                                />
                                                <span className="bank-active-slider" aria-hidden="true" />
                                            </label>
                                            <span>Is Active</span>
                                        </div>
                                    </div>
                                    <div className="bank-field qr-field">
                                        <span>QR Code <b>*</b></span>
                                        <div className={`qr-upload ${errors.qrCode ? "has-error" : ""}`}>
                                            {qrPreview ? (
                                                <>
                                                    <button type="button" className="qr-preview-button" onClick={() => setShowQrPreview(true)} aria-label="View QR code at full size">
                                                        <img src={qrPreview} alt="Uploaded bank QR code preview. Select to view full size." />
                                                    </button>
                                                    {!isViewMode && <button type="button" className="bank-button secondary" onClick={() => fileInputRef.current?.click()}>Replace</button>}
                                                </>
                                            ) : (
                                                <>
                                                    <QrCode className="qr-icon" aria-hidden="true" />
                                                    <span>No QR code uploaded</span>
                                                    {!isViewMode && <button type="button" className="bank-button secondary" onClick={() => fileInputRef.current?.click()}>Upload QR Code</button>}
                                                </>
                                            )}
                                            <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" onChange={handleQrChange} hidden disabled={isViewMode} />
                                        </div>
                                        {errors.qrCode && <small>{errors.qrCode}</small>}
                                    </div>
                                </div>
                            </section>
                        )}

                        {notice && <div className={`bank-notice ${noticeType}`} role="alert">{notice}</div>}
                        <div className="bank-form-actions">
                            <button type="button" className="bank-button secondary" onClick={cancelForm}>Cancel</button>
                            {!isViewMode && <button type="submit" className="bank-button primary" disabled={loading || (Boolean(detailId) && !isUpdateDirty)}>{detailId ? "Update" : "Save"}</button>}
                        </div>
                    </form>

            {showQrPreview && qrPreview && (
                <div className="qr-preview-backdrop" role="presentation" onClick={(event) => {
                    if (event.target === event.currentTarget) setShowQrPreview(false);
                }}>
                    <section className="qr-preview-modal" role="dialog" aria-modal="true" aria-label="QR code preview">
                        <button type="button" className="bank-modal-close" aria-label="Close QR code preview" onClick={() => setShowQrPreview(false)}>×</button>
                        <img src={qrPreview} alt="Full-size bank QR code" />
                        <p>Bank QR Code</p>
                    </section>
                </div>
            )}

            {showOtp && (
                <div className="bank-modal-backdrop" role="presentation">
                    <section className="bank-otp-modal" role="dialog" aria-modal="true" aria-labelledby="otp-title">
                        <button className="bank-modal-close" type="button" aria-label="Cancel verification" onClick={() => { setShowOtp(false); setOtp(""); setNotice(""); otpExpiresAtRef.current = null; }} disabled={loading}>×</button>
                        <div className="otp-lock" aria-hidden="true">✓</div>
                        <h2 id="otp-title">Verify Mobile Number</h2>
                        <p>Enter the 6-digit OTP sent to the registered mobile number in Profile Master.</p>
                        <div className="otp-selection">{companies.find((item) => item.id === companyId)?.name} · {branches.find((item) => item.id === branchId)?.name}</div>
                        <label className="bank-field otp-field">
                            <span>One-Time Password</span>
                            <input autoFocus value={otp} inputMode="numeric" maxLength={6} onChange={(event) => setOtp(event.target.value.replace(/\D/g, ""))} aria-label="6-digit OTP" disabled={loading} />
                        </label>
                        {otpStatus !== "verified" && otpStatus !== "saving" && otpRemainingSeconds > 0 && (
                            <div className="otp-countdown" role="status">
                                {`OTP expires in ${String(Math.floor(otpRemainingSeconds / 60)).padStart(2, "0")}:${String(otpRemainingSeconds % 60).padStart(2, "0")}`}
                            </div>
                        )}
                        <button type="button" className="otp-resend" onClick={() => { void sendOtp(); }} disabled={loading || otpRemainingSeconds > 0 || otpStatus === "verified" || otpStatus === "saving"}>Resend OTP</button>
                        <div className="bank-form-actions">
                            <button type="button" className="bank-button secondary" onClick={() => { setShowOtp(false); setOtp(""); setNotice(""); otpExpiresAtRef.current = null; }} disabled={loading}>Cancel</button>
                            <button type="button" className="bank-button primary" onClick={handleVerify} disabled={loading}>
                                Verify &amp; Save
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </main>
    );
};

export default BranchBankDetails;
